import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import inventoryApi from '../../../services/inventoryApi';;
import ReturnTypesForm from './ReturnTypesForm.jsx';
import PermissionGuard from '../../Common/PermissionGuard';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../Common/LoadingSpinner.jsx';

const ReturnTypesList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [returnTypes, setReturnTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('returnTypesManagementView') || 'card';
    } catch (error) {
      return 'card';
    }
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingReturnType, setEditingReturnType] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const returnTypeStatuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'DEPRECATED', label: 'Deprecated' }
  ];

  useEffect(() => {
    fetchReturnTypes();
  }, []);

  const fetchReturnTypes = async () => {
    // Check read permission before making API call
    if (!hasPermission('INVENTORY:RETURN_TYPES:READ')) {
      toast.error(error?.response?.data?.message || 'You do not have permission to view return types');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await inventoryApi.getReturnTypes();
      setReturnTypes(response.data || []);
    } catch (err) {
      console.error('Error fetching return types:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch return types');
      setReturnTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('returnTypesManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCreateReturnType = () => {
    if (!hasPermission('INVENTORY:RETURN_TYPES:CREATE')) {
      toast.error(error?.response?.data?.message || 'You do not have permission to create return types');
      return;
    }
    setEditingReturnType(null);
    setShowForm(true);
  };

  const handleEditReturnType = (returnType) => {
    if (!hasPermission('INVENTORY:RETURN_TYPES:UPDATE')) {
      toast.error(error?.response?.data?.message || 'You do not have permission to edit return types');
      return;
    }
    setEditingReturnType(returnType);
    setShowForm(true);
  };

  const handleDeleteReturnType = async (returnType) => {
    if (!hasPermission('INVENTORY:RETURN_TYPES:DELETE')) {
      toast.error(error?.response?.data?.message || 'You do not have permission to delete return types');
      return;
    }

    try {
      await inventoryApi.deleteReturnType(returnType._id);
      setShowDeleteConfirm(null);
      await fetchReturnTypes();
    } catch (error) {
      console.error('Failed to delete return type:', error);
      toast.error(error.response?.data?.message || 'Failed to delete return type');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingReturnType(null);
    fetchReturnTypes();
  };

  const clearError = () => {
  };

  // Filter return types
  const filteredReturnTypes = returnTypes.filter(returnType => {
    const matchesSearch = !searchTerm ||
      returnType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      returnType.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (returnType.description && returnType.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || returnType.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalItems = filteredReturnTypes.length;
  const totalPages = Math.ceil(totalItems / pagination.limit);
  const startIndex = (pagination.page - 1) * pagination.limit;
  const endIndex = startIndex + pagination.limit;
  const paginatedReturnTypes = filteredReturnTypes.slice(startIndex, endIndex);

  // Update pagination when filtered data changes
  React.useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: totalItems,
      pages: totalPages
    }));
  }, [totalItems, totalPages]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ page: 1, limit: newLimit, total: prev.total, pages: Math.ceil(prev.total / newLimit) }));
  };

  const getReturnTypeIcon = (returnType) => {
    return returnType.code.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'DEPRECATED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const renderReturnTypeCard = (returnType) => {
    return (
      <div key={returnType._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-32 w-full">
        <div className="p-3 h-full flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                {getReturnTypeIcon(returnType)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {returnType.name}
                </h3>
                <p className="text-xs text-gray-500 truncate">{returnType.code}</p>
              </div>
            </div>

            <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              <PermissionGuard permission="INVENTORY:RETURN_TYPES:UPDATE">
                <button
                  onClick={() => handleEditReturnType(returnType)}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                  title="Edit return type"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="INVENTORY:RETURN_TYPES:DELETE">
                <button
                  onClick={() => setShowDeleteConfirm(returnType)}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                  title="Delete return type"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </PermissionGuard>
            </div>
          </div>

          {returnType.description && (
            <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-tight flex-1">
              {returnType.description}
            </p>
          )}

          <div className="flex flex-wrap gap-1 mt-auto">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusColor(returnType.status)}`}>
              {returnType.status}
            </span>
            {returnType.reasonCodes && returnType.reasonCodes.length > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                {returnType.reasonCodes.length} reasons
              </span>
            )}
            {returnType.restockFee?.enabled && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                Restock Fee
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderReturnTypeRow = (returnType) => {
    return (
      <tr key={returnType._id} className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-sm font-semibold text-indigo-700">
              {getReturnTypeIcon(returnType)}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">{returnType.name}</div>
              <div className="text-xs text-gray-500">{returnType.code}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            {returnType.reasonCodes?.length || 0} reason codes
          </div>
          <div className="text-xs text-gray-500">
            {returnType.warrantyDays ? `${returnType.warrantyDays} days` : 'No warranty'}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            {returnType.restockFee?.enabled ?
              `${returnType.restockFee.type === 'PERCENTAGE' ? returnType.restockFee.value + '%' : '$' + returnType.restockFee.value}` :
              'No fee'
            }
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(returnType.status)}`}>
            {returnType.status}
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2">
            <PermissionGuard permission="INVENTORY:RETURN_TYPES:UPDATE">
              <button
                onClick={() => handleEditReturnType(returnType)}
                className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-all"
                title="Edit return type"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </PermissionGuard>

            <PermissionGuard permission="INVENTORY:RETURN_TYPES:DELETE">
              <button
                onClick={() => setShowDeleteConfirm(returnType)}
                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                title="Delete return type"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </PermissionGuard>
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <PermissionGuard
      permission="INVENTORY:RETURN_TYPES:READ"
      fallback={
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">You don't have permission to view return types.</p>
          </div>
        </div>
      }
    >
      <div className="bg-white shadow-sm border border-gray-200">
          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-red-800">{error}</p>
                <button
                  onClick={clearError}
                  className="ml-auto text-red-600 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Form Modal */}
          {showForm && (
            <ReturnTypesForm
              returnType={editingReturnType}
              onSuccess={handleFormSuccess}
              onCancel={() => {
                setShowForm(false);
                setEditingReturnType(null);
              }}
            />
          )}

          {/* Main Content */}
          {!showForm && (
            <>
              {/* Filters and Search */}
              <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                  {/* Search */}
                  <div className="flex-1">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Search return types by name or code..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="block w-full pl-10 pr-3 py-1.5 text-sm bg-white border border-gray-300 shadow-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Filters and Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    {/* Status Filter */}
                    <select
                      value={statusFilter}
                      onChange={handleStatusFilter}
                      className="text-sm border border-gray-300 rounded-sm bg-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[140px]"
                    >
                      {returnTypeStatuses.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>

                    {/* View Toggle */}
                    <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                      <button
                        onClick={() => handleViewChange('card')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        title="Card view"
                      >
                        <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                        </svg>
                        Cards
                      </button>
                      <button
                        onClick={() => handleViewChange('list')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        title="List view"
                      >
                        <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        List
                      </button>
                    </div>

                    {/* Add Return Type Button */}
                    <PermissionGuard permission="INVENTORY:RETURN_TYPES:CREATE">
                      <button
                        onClick={handleCreateReturnType}
                        className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Return Type
                      </button>
                    </PermissionGuard>
                  </div>
                </div>
              </div>
              {/* Pagination */}
              {filteredReturnTypes.length > 0 && (
                <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-t border-gray-200 flex-shrink-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Results Info */}
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                        </svg>
                      </div>
                      <div className="text-sm flex items-center gap-2">
                        <div>
                          <span className="font-semibold text-gray-900">
                            {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)}
                          </span>
                          <span className="text-gray-500 mx-1">of</span>
                          <span className="font-semibold text-gray-900">{pagination.total}</span>
                          <span className="text-gray-500 ml-1">return types</span>
                        </div>
                        {loading && (
                          <div className="flex items-center">
                            <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center gap-3">
                      {/* Items per page */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 font-medium">Show:</span>
                        <select
                          value={pagination.limit}
                          onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                          disabled={loading}
                          className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[70px]"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>

                      {/* Navigation Buttons */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={pagination.page <= 1 || loading}
                          className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          title="First page"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                          </svg>
                        </button>

                        <button
                          onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                          disabled={pagination.page <= 1 || loading}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          <span className="hidden sm:inline">Previous</span>
                        </button>

                        {/* Page Numbers */}
                        <div className="hidden md:flex items-center gap-1">
                          {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                            const pageNum = i + 1;
                            const isCurrentPage = pageNum === pagination.page;

                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                disabled={loading}
                                className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${isCurrentPage
                                  ? 'bg-indigo-600 text-white shadow-sm'
                                  : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40'
                                  }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          {pagination.pages > 7 && (
                            <span className="px-2 text-gray-400 text-sm">...</span>
                          )}
                        </div>

                        {/* Mobile page indicator */}
                        <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                          <span className="text-sm font-medium text-gray-900">{pagination.page}</span>
                          <span className="text-sm text-gray-500 mx-1">of</span>
                          <span className="text-sm font-medium text-gray-900">{pagination.pages}</span>
                        </div>

                        <button
                          onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                          disabled={pagination.page >= pagination.pages || loading}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                        >
                          <span className="hidden sm:inline">Next</span>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        <button
                          onClick={() => handlePageChange(pagination.pages)}
                          disabled={pagination.page >= pagination.pages || loading}
                          className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          title="Last page"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Content */}
              <div className="p-2">
                {filteredReturnTypes.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No return types found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all' ?
                        'Try adjusting your search or filter criteria.' :
                        'Get started by creating your first return type.'
                      }
                    </p>
                    {!searchTerm && statusFilter === 'all' && (
                      <div className="mt-6">
                        <PermissionGuard permission="INVENTORY:RETURN_TYPES:CREATE">
                          <button
                            onClick={handleCreateReturnType}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Create Return Type
                          </button>
                        </PermissionGuard>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Card View */}
                    {view === 'card' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {paginatedReturnTypes.map(renderReturnTypeCard)}
                      </div>
                    )}

                    {/* List View */}
                    {view === 'list' && (
                      <div className="overflow-x-auto">
                        <table className="min-w-max w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Return Type
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Details
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Restock Fee
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedReturnTypes.map(renderReturnTypeRow)}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
              <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>

                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Return Type</h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to delete <span className="font-medium text-gray-900">"{showDeleteConfirm.name}"</span>?
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      This action cannot be undone.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteReturnType(showDeleteConfirm)}
                      className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded transition-colors"
                    >
                      Delete Return Type
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </PermissionGuard>
  );
};

export default ReturnTypesList;